import {
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { IEcdis, IVessel } from "interfaces";
import React from "react";

export const EcdisList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IEcdis>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } = useExport<IEcdis>({
    mapData: (item) => {
      return {
        "ECDIS Make": item?.make || "",
        "ECDIS Model": item?.model || "",
        "UKHO Licence ID": item?.vessel?.licence?.ukhoLicenceId || "",
        "Vessel IMO Number": item?.vessel?.imoNumber || "",
        "Vessel Name": item?.vessel?.name || "",
      };
    },
    unparseConfig: {
      header: true,
      columns: [
        "ECDIS Make",
        "ECDIS Model",
        "UKHO Licence ID",
        "Vessel IMO Number",
        "Vessel Name",
      ],
    },
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselNameSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",
    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IEcdis> = [
    {
      title: "IMO Number",
      dataIndex: ["vessel", "imoNumber"],
      key: "vessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["vessel", "name"],
      key: "vessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel Name"
            {...vesselNameSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Make",
      dataIndex: "make",
      key: "make",
      width: 150,
      render: (value) => <TextField value={value} />,
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      width: 150,
      render: (value) => <TextField value={value} />,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        subTitle: "active ecdis",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
